import {
  assertMergeSources,
  assertSplitAmount,
  assertSplitSource,
  assertTransferRecipient,
} from './coin-arguments';
import {
  type ParsedCallArg,
  type ParsedCommand,
  type ParsedSplitCommand,
  SendAssertionError,
  type SendExpectation,
  type TransferValidationStrategy,
} from './transaction-validation';

const ALLOWED_COMMANDS = new Set(['MergeCoins', 'SplitCoins', 'TransferObjects']);

export const assertCoinObjectTransfer: TransferValidationStrategy = ({
  commands,
  inputs,
  gasOwner,
  expected,
}) => {
  assertAllowedCommands(commands);
  assertMergeSources(commands, inputs);
  const split = commands.find(
    (command): command is ParsedSplitCommand => command.$kind === 'SplitCoins',
  );
  if (!split) throw new SendAssertionError('missing split');
  assertSplitSource(split, inputs, gasOwner, expected);
  assertRecipientAndAmount(commands, inputs, expected);
};

/**
 * The split is required, not optional: a coin's balance is never encoded in
 * `TransactionData`, so a transfer without one carries no amount to check and
 * could move an owned coin of any size unchallenged.
 */
function assertAllowedCommands(commands: ParsedCommand[]): void {
  let transferCount = 0;
  let splitCount = 0;
  for (const command of commands) {
    if (!ALLOWED_COMMANDS.has(command.$kind)) {
      throw new SendAssertionError(`it contains an unexpected operation (${command.$kind})`);
    }
    if (command.$kind === 'TransferObjects') transferCount += 1;
    if (command.$kind === 'SplitCoins') splitCount += 1;
  }
  if (transferCount !== 1) {
    throw new SendAssertionError('it must contain exactly one transfer');
  }
  if (splitCount !== 1) {
    throw new SendAssertionError('it must split off the exact amount before transferring');
  }
}

/**
 * Traces the transferred object back to the split that produced it. Checking
 * the split's declared amount alone would let a decoy split for the approved
 * amount sit alongside a transfer of a different, larger coin.
 */
function assertRecipientAndAmount(
  commands: ParsedCommand[],
  inputs: ParsedCallArg[],
  expected: SendExpectation,
): void {
  const transferCommand = commands.find(
    (command): command is Extract<ParsedCommand, { $kind: 'TransferObjects' }> =>
      command.$kind === 'TransferObjects',
  );
  if (!transferCommand) {
    // assertAllowedCommands already guarantees exactly one exists.
    throw new SendAssertionError('missing transfer');
  }
  const { objects, address } = transferCommand.TransferObjects;

  assertTransferRecipient(address, inputs, expected.recipient);

  if (objects.length !== 1) {
    throw new SendAssertionError('it transfers more than one object in a single send');
  }
  const [transferred] = objects;

  if (transferred.$kind === 'NestedResult') {
    const [sourceIndex] = transferred.NestedResult;
    const source = commands[sourceIndex];
    if (source?.$kind === 'SplitCoins') {
      assertSplitAmount(source, inputs, expected.amount);
      return;
    }
  }

  throw new SendAssertionError('it transfers an object not produced by a split you approved');
}
